/**
 * Entrega un PDF (o cualquier blob) al usuario adaptándose al dispositivo:
 *
 *  - En laptop / desktop: abre el blob en una pestaña nueva (previsualización).
 *    Si el navegador bloquea el popup, cae a descarga directa.
 *  - En celular / tablet: fuerza la descarga directa del archivo, porque los
 *    navegadores móviles no manejan bien las URLs `blob:` en pestañas nuevas
 *    (se quedan en blanco o no permiten guardar).
 *
 * Uso:
 *   import { deliverPdf } from "@helpers";
 *   deliverPdf(blob, "factura-001.pdf");
 */

export const isMobileOrTablet = (): boolean => {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent || (navigator as any).vendor || "";

  // iPadOS 13+ se reporta como Mac; se detecta por soporte táctil.
  const iPadOS =
    /Macintosh/.test(ua) &&
    typeof document !== "undefined" &&
    "ontouchend" in document;

  const mobileUA =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet|Silk|Kindle|PlayBook/i.test(
      ua,
    );

  const coarsePointer =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches;

  return iPadOS || mobileUA || coarsePointer;
};

const triggerDownload = (url: string, filename: string) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/**
 * @param blob      contenido del archivo
 * @param filename  nombre sugerido para la descarga (con extensión, ej. "doc.pdf")
 * @param opts.forceDownload  fuerza la descarga incluso en desktop
 */
export const deliverPdf = (
  blob: Blob,
  filename: string,
  opts: { forceDownload?: boolean } = {},
): void => {
  const url = URL.createObjectURL(blob);
  const cleanup = () => setTimeout(() => URL.revokeObjectURL(url), 60_000);

  if (opts.forceDownload || isMobileOrTablet()) {
    triggerDownload(url, filename);
    cleanup();
    return;
  }

  const tab = window.open(url, "_blank", "noopener,noreferrer");
  if (tab) {
    cleanup();
  } else {
    // popup bloqueado -> descarga
    triggerDownload(url, filename);
    cleanup();
  }
};

/**
 * Igual que `deliverPdf` pero recibe una URL ya construida (por ejemplo la que
 * genera `usePDF` de @react-pdf/renderer). No revoca la URL porque su ciclo de
 * vida lo maneja quien la creó.
 *
 * Devuelve `true` si abrió pestaña, `false` si disparó descarga.
 */
export const deliverPdfFromUrl = (
  url: string,
  filename: string,
  opts: { forceDownload?: boolean } = {},
): boolean => {
  if (opts.forceDownload || isMobileOrTablet()) {
    triggerDownload(url, filename);
    return false;
  }

  const tab = window.open(url, "_blank", "noopener,noreferrer");
  if (!tab) {
    triggerDownload(url, filename);
    return false;
  }
  return true;
};
